/**
 * The county map-layer files: what the per-parcel map layers say about each
 * parcel of a county, for Pro (`mapLayers`).
 *
 *   layers/{COUNTY}_layers.arrow.gz   one row per parcel any layer touches
 *   layers/manifest.json              which counties have a file, row counts
 *
 * Cut from MotherDuck's `parcel_layers` view by
 * `scripts/export_parcel_layers.py`: one row per parcel that waterfront,
 * wetlands, floodplain or the proposed 765-kV line touches, NULL where a layer
 * has nothing, joined to the county's parcel file on `parcel_id`. A parcel
 * with no row has none of the four. Gzipped Arrow like the parcel files, so it
 * is read the same way (`county-arrow.ts`) and is small beside them: Dane
 * ~0.34 MB against its 9.6 MB parcel file.
 *
 * The exporter holds the same column list, and `parcel-layers-contract.test.ts`
 * fails if the two disagree. `PARCEL_LAYERS_VERSION` is stamped into each
 * file's schema metadata (`layers_version`) and the manifest; it changes when
 * a column changes meaning or type.
 *
 * Imports nothing, like `county-list.ts`, so a worker can import it too.
 */

export const PARCEL_LAYERS_VERSION = 1;
export const PARCEL_LAYERS_PREFIX = "layers/";

/** Column → how it is stored. `dict` is a dictionary-encoded string. */
export const PARCEL_LAYER_COLUMNS = {
  parcel_id: "string",
  waterfront_type: "dict",
  waterfront_ft: "float32",
  primary_water_name: "dict",
  primary_water_wbic: "int32",
  /** Every named water body the parcel touches, longest frontage first: "wbic|name|type;…". */
  water_bodies: "dict",
  /** "dnr", or "naip-2022" for water DNR never mapped, traced from that year's aerial imagery. */
  primary_water_source: "dict",
  on_lake: "bool",
  on_pond: "bool",
  on_river: "bool",
  on_stream: "bool",
  stream_through: "bool",
  wetland_acres: "float32",
  wetland_pct: "float32",
  main_wetland_type: "dict",
  wetland_types: "dict",
  flood_acres: "float32",
  flood_pct: "float32",
  flood_zones: "dict",
  in_floodway: "bool",
  tx765_nearest_ft: "float32",
  tx765_maribell_ft: "float32",
  tx765_beci_ft: "float32",
  tx765_beci_segment: "dict",
  tx765_crossed_by: "dict",
} as const;

type StoredAs = { string: string; dict: string; float32: number; int32: number; bool: boolean };
type Columns = typeof PARCEL_LAYER_COLUMNS;

/** One parcel's row, as `tableFromIPC(...).get(i).toJSON()` returns it. */
export type ParcelLayerRow = { [K in keyof Columns]: StoredAs[Columns[K]] | null } & { parcel_id: string };

/**
 * Where the app asks for a county's file: the Pages Function that checks the
 * caller's plan (functions/api/layers/[file].ts), same origin. The files live
 * under `PARCEL_LAYERS_PREFIX` in a private bucket with no public URL — the
 * public data domain would serve them to anyone who read this line.
 */
export const PARCEL_LAYERS_API = "/api/layers/";

export function parcelLayersUrl(countySlug: string): string {
  return `${PARCEL_LAYERS_API}${countySlug.toUpperCase()}_layers.arrow.gz`;
}

export function parcelLayersManifestUrl(): string {
  return `${PARCEL_LAYERS_API}manifest.json`;
}

// ── What the county map does with them ──────────────────────────────────────
// The filters below mean what the AI tools' filters mean (workers/mcp
// overlays.ts), so a parcel the site calls "on a lake" is one ChatGPT does too.

/** Counties with no effective FEMA digital flood map: no parcel there has a
 *  floodplain row, so "outside the floodplain" would call every one of them dry. */
export const FLOOD_UNMAPPED_COUNTIES: readonly string[] = ["ashland", "marinette", "menominee", "price", "sawyer"];

/** The layer files hold 765-kV distances out to 2 miles and no farther. */
export const TRANSMISSION_MAX_MILES = 2;
const FEET_PER_MILE = 5280;

/** The water types the county map offers. "reservoir" (under 2% of waterfront
 *  parcels) is still matched by "any water". */
export const WATER_FILTER_TYPES = ["lake", "river", "stream", "pond"] as const;
export type WaterFilterType = (typeof WATER_FILTER_TYPES)[number];

/** The 765-kV distances offered, in miles; 0 is "the line crosses it". */
export const TX765_FILTER_MILES = [0, 0.25, 0.5, 1, 2] as const;

export interface LayerFilters {
  /**
   * On water: any of `types` (none listed = any water), with at least this
   * much frontage, and — when `bodies` lists any — on one of those water
   * bodies (DNR WBICs), whether or not it is the parcel's main one.
   */
  water?: { types: WaterFilterType[]; minFrontageFt: number; bodies?: number[] };
  /** In, or wholly outside, FEMA's 1%-annual-chance ("100-year") floodplain. */
  flood?: "in" | "out";
  /** Some mapped wetland (at least `minPct` of the parcel), or none mapped. */
  wetland?: { mode: "some" | "none"; minPct: number };
  /** Within this many miles of the proposed 765-kV line, from the parcel's edge. */
  tx765?: { withinMiles: number };
}

export function hasLayerFilters(filters: LayerFilters | undefined): boolean {
  return !!filters && !!(filters.water || filters.flood || filters.wetland || filters.tx765);
}

/** URL params, in the order they are written. */
export const LAYER_PARAM_KEYS = ["water", "frontage", "waterBody", "flood", "wetland", "wetlandPct", "tx765"] as const;

export function encodeLayerFilters(filters: LayerFilters | undefined, params: URLSearchParams): void {
  if (!filters) return;
  if (filters.water) {
    params.set("water", filters.water.types.length > 0 ? filters.water.types.join(",") : "any");
    if (filters.water.minFrontageFt > 0) params.set("frontage", String(filters.water.minFrontageFt));
    if (filters.water.bodies?.length) params.set("waterBody", filters.water.bodies.join(","));
  }
  if (filters.flood) params.set("flood", filters.flood);
  if (filters.wetland) {
    params.set("wetland", filters.wetland.mode);
    if (filters.wetland.mode === "some" && filters.wetland.minPct > 0) {
      params.set("wetlandPct", String(filters.wetland.minPct));
    }
  }
  if (filters.tx765) params.set("tx765", String(filters.tx765.withinMiles));
}

const nonNegative = (raw: string | null): number => {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** The layer filters a link carries, or undefined. Values it cannot read are dropped, never guessed. */
export function decodeLayerFilters(params: URLSearchParams): LayerFilters | undefined {
  const filters: LayerFilters = {};
  const water = params.get("water");
  if (water) {
    const types = water
      .split(",")
      .filter((t): t is WaterFilterType => (WATER_FILTER_TYPES as readonly string[]).includes(t));
    if (water === "any" || types.length > 0) {
      filters.water = { types, minFrontageFt: nonNegative(params.get("frontage")) };
      const bodies = (params.get("waterBody") ?? "")
        .split(",")
        .map(Number)
        .filter((n) => Number.isInteger(n) && n > 0);
      if (bodies.length > 0) filters.water.bodies = [...new Set(bodies)];
    }
  }
  const flood = params.get("flood");
  if (flood === "in" || flood === "out") filters.flood = flood;
  const wetland = params.get("wetland");
  if (wetland === "some" || wetland === "none") {
    filters.wetland = { mode: wetland, minPct: wetland === "some" ? Math.min(100, nonNegative(params.get("wetlandPct"))) : 0 };
  }
  const tx765 = params.get("tx765");
  if (tx765 !== null && tx765 !== "") {
    const miles = Number(tx765);
    if (Number.isFinite(miles) && miles >= 0) filters.tx765 = { withinMiles: Math.min(miles, TRANSMISSION_MAX_MILES) };
  }
  return hasLayerFilters(filters) ? filters : undefined;
}

/** One named water body a parcel touches. `wbic` is DNR's Water Body ID Code. */
export interface WaterBody {
  wbic: number;
  name: string;
  /** "lake", "pond", "river", "stream" or "reservoir". */
  type: string;
}

/**
 * The parcel's named water bodies, longest frontage first. From
 * `water_bodies` ("wbic|name|type;..."); a file cut before that column existed
 * falls back to the main water body alone.
 */
export function waterBodiesOf(row: ParcelLayerRow | undefined): WaterBody[] {
  if (!row) return [];
  if (row.water_bodies) {
    const bodies: WaterBody[] = [];
    for (const entry of row.water_bodies.split(";")) {
      const [wbic, name, type] = entry.split("|");
      const id = Number(wbic);
      if (Number.isInteger(id) && id > 0 && name) bodies.push({ wbic: id, name, type: type || "water" });
    }
    return bodies;
  }
  const name = row.primary_water_name;
  if (row.primary_water_wbic && name && name !== "Unnamed") {
    return [{ wbic: row.primary_water_wbic, name, type: row.waterfront_type ?? "water" }];
  }
  return [];
}

/** A water body in the county's picker. */
export interface WaterBodyChoice extends WaterBody {
  /** Parcels touching it. */
  parcels: number;
  /** Where most of those parcels are, to tell apart water bodies that share a name. */
  place?: string;
  /** Another water body in the county has the same name. */
  sharedName: boolean;
}

/**
 * Every named water body in the county's layer file, most parcels first.
 * `placeOf` gives a parcel's municipality; with it, a name used twice (Vilas
 * has three Mud Lakes) carries the place most of its parcels are in.
 */
export function listWaterBodies(
  rows: Map<string, ParcelLayerRow>,
  placeOf?: (parcelId: string) => string | null | undefined,
): WaterBodyChoice[] {
  const byId = new Map<number, { body: WaterBody; parcels: number; places: Map<string, number> }>();
  for (const [parcelId, row] of rows) {
    for (const body of waterBodiesOf(row)) {
      let entry = byId.get(body.wbic);
      if (!entry) {
        entry = { body, parcels: 0, places: new Map() };
        byId.set(body.wbic, entry);
      }
      entry.parcels++;
      const place = placeOf?.(parcelId);
      if (place) entry.places.set(place, (entry.places.get(place) ?? 0) + 1);
    }
  }
  const nameCount = new Map<string, number>();
  for (const { body } of byId.values()) nameCount.set(body.name, (nameCount.get(body.name) ?? 0) + 1);
  return [...byId.values()]
    .map(({ body, parcels, places }) => {
      let place: string | undefined;
      let most = 0;
      for (const [name, count] of places) if (count > most) [place, most] = [name, count];
      return { ...body, parcels, place, sharedName: (nameCount.get(body.name) ?? 0) > 1 };
    })
    .sort((a, b) => b.parcels - a.parcels || a.name.localeCompare(b.name));
}

const WATER_TYPE_MATCH: Record<WaterFilterType, (row: ParcelLayerRow) => boolean> = {
  lake: (row) => row.on_lake === true,
  river: (row) => row.on_river === true,
  stream: (row) => row.on_stream === true || row.stream_through === true,
  pond: (row) => row.on_pond === true,
};

export interface LayerScope {
  countySlug: string;
  /** Whether the 765-kV line comes within 2 miles of any parcel in the county. */
  hasTransmission: boolean;
}

/**
 * A predicate over one parcel's layer row (undefined: no layer touches the
 * parcel), or null when no layer filter applies.
 *
 * A filter the county cannot answer is ignored rather than applied, like the
 * value filter where a county has no values: the floodplain in a county with
 * no FEMA map, the 765-kV line where it comes nowhere near. A link carrying
 * one must not empty the map with no control on screen to explain it.
 */
export function layerMatcher(
  filters: LayerFilters | undefined,
  scope: LayerScope,
): ((row: ParcelLayerRow | undefined) => boolean) | null {
  if (!filters) return null;
  const { water, wetland, tx765 } = filters;
  const flood = FLOOD_UNMAPPED_COUNTIES.includes(scope.countySlug) ? undefined : filters.flood;
  const tx = scope.hasTransmission ? tx765 : undefined;
  if (!water && !flood && !wetland && !tx) return null;
  const typeTests = water?.types.map((t) => WATER_TYPE_MATCH[t]) ?? [];
  const bodySet = water?.bodies?.length ? new Set(water.bodies) : null;
  const txFeet = tx ? tx.withinMiles * FEET_PER_MILE : 0;

  return (row) => {
    if (water) {
      if (!row || row.waterfront_type == null) return false;
      if (typeTests.length > 0 && !typeTests.some((test) => test(row))) return false;
      if (water.minFrontageFt > 0 && (row.waterfront_ft ?? 0) < water.minFrontageFt) return false;
      if (bodySet && !waterBodiesOf(row).some((body) => bodySet.has(body.wbic))) return false;
    }
    if (flood) {
      const inFlood = row?.flood_acres != null;
      if (inFlood !== (flood === "in")) return false;
    }
    if (wetland) {
      const hasWetland = row?.wetland_acres != null;
      if (wetland.mode === "none") {
        if (hasWetland) return false;
      } else if (!hasWetland || (wetland.minPct > 0 && (row!.wetland_pct ?? 0) < wetland.minPct)) {
        return false;
      }
    }
    if (tx) {
      if (row?.tx765_nearest_ft == null || row.tx765_nearest_ft > txFeet) return false;
    }
    return true;
  };
}

/** One line of the parcel card's "Water & land" block. */
export interface LayerFactLine {
  layer: "water" | "flood" | "wetland" | "tx765";
  text: string;
  /** A layer with nothing on this parcel, shown quieter. */
  none?: boolean;
}

const feet = (n: number) => `${Math.round(n).toLocaleString("en-US")} ft`;
const pct = (n: number) => (n > 0 && n < 1 ? "<1%" : `${Math.round(n)}%`);
const acres = (n: number) => `${n < 10 ? n.toFixed(2).replace(/\.?0+$/, "") : Math.round(n).toLocaleString("en-US")} ac`;
const miles = (ft: number) => {
  const mi = ft / FEET_PER_MILE;
  // Number() drops trailing zeros and a bare point: "1.0" is "1", never "1.".
  return mi < 0.1 ? feet(ft) : `${Number(mi.toFixed(mi < 1 ? 2 : 1))} mi`;
};

function waterName(row: ParcelLayerRow): string {
  const type = row.waterfront_type ?? "water";
  const name = row.primary_water_name;
  const traced = /^naip-(\d{4})$/.exec(row.primary_water_source ?? "");
  // Water DNR never mapped (a lake dug for a subdivision): say where it came from.
  if (traced) return `an unnamed ${type} (traced from ${traced[1]} aerial imagery)`;
  return !name || name === "Unnamed" ? `an unnamed ${type}` : name;
}

/**
 * What the layers say about a parcel in a few words each, for a row of
 * results: only the layers that touch it, so a parcel with none shows
 * nothing. The card's full sentences are `describeLayerFacts`.
 */
export function shortLayerFacts(row: Partial<ParcelLayerRow> | null | undefined): LayerFactLine[] {
  if (!row) return [];
  const lines: LayerFactLine[] = [];
  if (row.waterfront_type != null) {
    const name = row.primary_water_name && row.primary_water_name !== "Unnamed" ? row.primary_water_name : null;
    const where = name ?? `${/^naip-/.test(row.primary_water_source ?? "") ? "unmapped" : "unnamed"} ${row.waterfront_type}`;
    lines.push({ layer: "water", text: `${where} · ${feet(row.waterfront_ft ?? 0)}` });
  }
  if (row.flood_acres != null) lines.push({ layer: "flood", text: `${pct(row.flood_pct ?? 0)} floodplain` });
  if (row.wetland_acres != null) lines.push({ layer: "wetland", text: `${pct(row.wetland_pct ?? 0)} wetland` });
  if (row.tx765_nearest_ft != null) {
    lines.push({ layer: "tx765", text: row.tx765_crossed_by ? "765-kV crosses" : `765-kV ${miles(row.tx765_nearest_ft)}` });
  }
  return lines;
}

/**
 * What the four layers say about a parcel, one line each, for the parcel card.
 * `row` undefined means no layer touches it, which is itself an answer: no
 * mapped water, wetland or floodplain.
 */
export function describeLayerFacts(row: ParcelLayerRow | undefined, scope: LayerScope): LayerFactLine[] {
  const lines: LayerFactLine[] = [];

  if (row?.waterfront_type != null) {
    const ft = row.waterfront_ft ?? 0;
    const main = waterName(row);
    // Frontage is the total along all water, so name the others too.
    const others = waterBodiesOf(row)
      .filter((body) => body.wbic !== row.primary_water_wbic)
      .map((body) => body.name);
    const also =
      others.length === 0 ? "" : `; also ${others.slice(0, 2).join(" and ")}${others.length > 2 ? ` and ${others.length - 2} more` : ""}`;
    lines.push({
      layer: "water",
      text: row.stream_through
        ? `${main[0].toUpperCase()}${main.slice(1)} runs through it (${feet(ft)})${also}`
        : `${feet(ft)} of frontage on ${main}${also}`,
    });
  } else {
    lines.push({ layer: "water", text: "No mapped water frontage", none: true });
  }

  if (FLOOD_UNMAPPED_COUNTIES.includes(scope.countySlug)) {
    lines.push({ layer: "flood", text: "No FEMA flood map for this county", none: true });
  } else if (row?.flood_acres != null) {
    const zones = row.flood_zones ? ` (zone ${row.flood_zones})` : "";
    lines.push({
      layer: "flood",
      text: `${pct(row.flood_pct ?? 0)} in the 100-year floodplain${zones}${row.in_floodway ? ", reaching the floodway" : ""}`,
    });
  } else {
    lines.push({ layer: "flood", text: "Outside the mapped 100-year floodplain", none: true });
  }

  if (row?.wetland_acres != null) {
    const kind = row.main_wetland_type ? `, mostly ${row.main_wetland_type}` : "";
    lines.push({ layer: "wetland", text: `${acres(row.wetland_acres)} mapped wetland (${pct(row.wetland_pct ?? 0)})${kind}` });
  } else {
    lines.push({ layer: "wetland", text: "No mapped wetland", none: true });
  }

  // Never "the line": BECI's half is candidate study segments (~604 mi of
  // them for a ~190-mi line) and MariBell's a proposed route not yet approved.
  if (scope.hasTransmission) {
    const segment = row?.tx765_beci_segment ? `765-kV study segment ${row.tx765_beci_segment}` : "a 765-kV study segment";
    const candidate = "(BECI; a candidate corridor, not a route)";
    if (row?.tx765_crossed_by) {
      lines.push({
        layer: "tx765",
        text: row.tx765_crossed_by.startsWith("MariBell")
          ? "MariBell's proposed 765-kV route crosses it (not yet approved)"
          : `${segment[0].toUpperCase()}${segment.slice(1)} crosses it ${candidate}`,
      });
    } else if (row?.tx765_nearest_ft != null) {
      lines.push({
        layer: "tx765",
        text: row.tx765_maribell_ft === row.tx765_nearest_ft
          ? `${miles(row.tx765_nearest_ft)} from MariBell's proposed 765-kV route (not yet approved)`
          : `${miles(row.tx765_nearest_ft)} from ${segment} ${candidate}`,
      });
    } else {
      lines.push({ layer: "tx765", text: "Over 2 mi from any 765-kV study segment or proposed route", none: true });
    }
  }

  return lines;
}
